import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginDigestGameData,
	DigestTopic,
	Article,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Digest specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class DigestGenerationService
	extends BaseGenerationService<PluginDigestGameData>
{
	readonly pluginType = PluginType.Digest;

	/**
	 * Create system and user prompts for digest generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		const systemPrompt = `Olet talkshown juontaja, joka kirjoittaa nykyään blogia. Tehtäväsi on analysoida lähdemateriaaleja ja luoda niistä kiinnostavia aiheita (topics), jotka käyttäjä voi valita lukeakseen. Jokainen aihe sisältää kysymyksen käyttäjälle, tiivistelmän ja pidemmän yksityiskohtaisen tekstin.

${targetLanguagePrompt}

Anna vastaus validoidussa JSON-muodossa.`;

		const userPrompt = `
		Tee tiedostohakuja lähdemateriaaliin hakutyökalulla löytääksesi artikkelien sisältöä ja yhteisiä teemoja.

		VAIHEITTAINEN PROSESSI:
		1. Tunnista kaikki artikkeleista löytyvät keskeiset aihepiirit
		2. Luo 3-7 aihetta (topic) jotka kattavat lähdemateriaalin
		3. Jokaiselle aiheelle seuraavat asiat:
		* kysymys: tämä on lukijalle esitettävä kysymys. Siihen pitää pystyä vastata kyllä tai ei. Kysymyksen tulee olla hauska ja rento. Vastauksen perusteella pitää pystyä päätellä, onko käyttäjä kiinnostunut kysymyksen esittämästä aiheesta.
		* tiivistelmä: tämän pitää olla n. 4-6 lauseen mittainen rennosti kirjoitettu kappale kyseisestä aiheesta. Tekstin tulee olla mukaansatempaavaa ja pakinamaista, mutta sen pitää kertoa selkeästi, mistä on kyse. Kappale voi alkaa talkshow/uutislähetys-tyylisesti, kuten esim. "Mitä tapahtuu kansainvälisen politiikan näyttämöllä?" tai "Ja sitten kotimaisiin tapahtumiin".
		* yksityiskohtainen teksti: jos käyttäjä klikkaa tiivistelmää, näytetään tämä teksti. Tämä teksti kertoo hyvin tarkasti, mistä artikkelissa on kyse, mitä siinä on kerrottu ja mitä on tapahtunut. Kirjoittamisen tyyli voi olla yhä rento ja leikkisä, mutta nyt pitää keskittyä kuitenkin enemmän itse faktasisältöön. Teksti saa kuitenkin olla puhekielistä.

		Palauta JSON-objekti, jossa on:
		- "topics" // (array) Lista aiheista. Jokainen topic sisältää:
		  - "id" (string) Uniikki tunniste (esim. "topic_1")
		  - "name" (string) Aiheen nimi (esim. "Politiikka", "Teknologia")
		  - "emoji" (string) Sopiva emoji aiheelle
		  - "question" (string) Kysymys käyttäjälle (esim. "Kiinnostaako sinua kansainvälinen politiikka?")
		  - "summary" (string) Lyhyt 4-6 lauseen tiivistelmä aiheesta lähdemateriaalin pohjalta
		  - "detailedText" (string) Pidempi, 5-10 lauseen teksti aiheesta.

		HUOM: Sisällön tulee perustua annettuun lähdemateriaaliin.`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into PluginDigestGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(response: any, parameters: any): GenerationResult {
		try {
			if (!response.json || !response.json.topics) {
				return {
					success: false,
					error: 'Virheellinen vastausrakenne - topics-kenttä puuttuu',
					rawResponse: response.rawResponse,
				};
			}

			// Create game data with generated topics
			const gameData = new PluginDigestGameData();
			gameData.topics = [];

			// Parse topics from AI response
			response.json.topics.forEach((t: any, index: number) => {
				const topic: DigestTopic = {
					id: t.id || `t_${Date.now()}_${index}`,
					name: t.name || 'Nimetön aihe',
					emoji: t.emoji || '📰',
					question: t.question || 'Kiinnostaako sinua tämä aihe?',
					summary: t.summary || 'Tiivistelmä puuttuu',
					detailedText:
						t.detailedText || 'Yksityiskohtainen teksti puuttuu',
				};
				gameData.topics.push(topic);
			});

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Generate simple content from articles when AI fails
	 * @param articles Articles to generate topics from
	 * @returns Simple game data with basic topics
	 */
	generateSimpleContent(articles: Article[]): PluginDigestGameData {
		const gameData = new PluginDigestGameData();
		gameData.topics = [];

		// Generate basic topics from articles
		articles.forEach((article: Article, index: number) => {
			const topic: DigestTopic = {
				id: `topic_${Date.now()}_${index}`,
				name: article.title || `Artikkeli ${index + 1}`,
				emoji: '📰',
				question: `Kiinnostaako sinua ${article.title || 'tämä aihe'}?`,
				summary: article.content
					? article.content.substring(0, 200) + '...'
					: 'Tiivistelmä ei saatavilla.',
				detailedText: article.content
					? article.content.substring(0, 500) + '...'
					: 'Yksityiskohtainen teksti ei saatavilla.',
			};
			gameData.topics.push(topic);
		});

		return gameData;
	}

	/**
	 * Validate digest specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.selectedArticles &&
			request.sourceMaterial.selectedArticles.length > 0;

		if (!hasSourceMaterial) {
			return {
				valid: false,
				error: 'Valitse vähintään yksi artikkeli lähteeksi',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for digest
	 */
	getDefaultParameters(): any {
		return {
			clearPrevious: true,
			numberOfTopics: 5,
		};
	}

	/**
	 * Merge new content with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: PluginDigestGameData,
		newGameData: PluginDigestGameData,
		clearPrevious: boolean,
	): PluginDigestGameData {
		if (clearPrevious) {
			// Replace all topics
			existingGameData.topics = [...newGameData.topics];
		} else {
			// Append new topics to existing ones
			existingGameData.topics = [
				...existingGameData.topics,
				...newGameData.topics,
			];
		}

		return existingGameData;
	}
}
